/**
 * DescriptionBottomSheet — bottom sheet editor for TikTok product description.
 *
 * Opens when user taps the description card. Contains a textarea with
 * the HTML description (plain text editing for v1, rich text later).
 * Changes are committed to the zustand store on close.
 */

import { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useTikTokEditStore, selectFieldValue } from "../stores/useTikTokEditStore";

const MAX_DESCRIPTION_LENGTH = 10000;

interface DescriptionBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DescriptionBottomSheet({
  open,
  onOpenChange,
}: DescriptionBottomSheetProps) {
  const currentValue = useTikTokEditStore(selectFieldValue("description"));
  const setField = useTikTokEditStore((s) => s.setField);
  const [localValue, setLocalValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync local value when sheet opens
  useEffect(() => {
    if (open) {
      setLocalValue(currentValue ?? "");
      // Focus textarea after sheet animation
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [open, currentValue]);

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      // Commit value to store on close
      setField("description", localValue);
    }
    onOpenChange(isOpen);
  };

  const charCount = localValue.length;
  const isOverLimit = charCount > MAX_DESCRIPTION_LENGTH;

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="flex flex-col">
        <SheetHeader className="pb-2">
          <SheetTitle className="flex items-center justify-between">
            <span>Edit Description</span>
            <span
              className={`text-sm font-normal ${isOverLimit ? "text-red-500" : "text-muted-foreground"}`}
            >
              {charCount.toLocaleString()}/{MAX_DESCRIPTION_LENGTH.toLocaleString()}
            </span>
          </SheetTitle>
        </SheetHeader>
        <textarea
          ref={textareaRef}
          className="flex-1 w-full resize-none rounded-sm border border-border bg-background p-3 text-sm font-mono outline-none focus:ring-2 focus:ring-primary/40"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder="Enter product description (HTML supported)..."
        />
        <p className="text-xs text-muted-foreground mt-2">
          TikTok accepts HTML formatting. Changes are saved when you close this sheet.
        </p>
      </SheetContent>
    </Sheet>
  );
}
